import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import ReCAPTCHA from "react-google-recaptcha";

import {
  NetworkError,
  fetchPublicAPI,
  useGET,
  usePOST,
} from "hooks/usePublicAPI";
import { useFirebase } from "firebase";
import { formatPhoneNumber } from "helpers/phone";
import { apiErrorCode } from "utils/errors";
import { freshRecaptchaToken } from "helpers/recaptcha";
import {
  endedCodeSendFailureMessage,
  grantFailureKind,
  grantFailureMessage,
  failureFallbackFor,
  smsRequestFailureMessage,
  smsVerifyFailureMessage,
} from "pages/Login/login-grant-flow";
import {
  otpSessionFields,
  recordOtpSession,
  restartOtpSessions,
  type OtpSessions,
} from "helpers/otp-sessions";
import AuthLayout from "layouts/AuthLayout";
import { SupportCallLine } from "pages/Login/GrantParts";
import OtpVerification from "components/OtpVerification";
import type {
  SendTrigger,
  SubmitOutcome,
} from "components/OtpVerification/ended-code";

const INVITE_FALLBACK = failureFallbackFor(false);

type InviteLookupResponse = {
  success: boolean;
  person_id: string;
  method: "email" | "sms";
  invite_id: string;
  phone_number?: string; // Only present for SMS
  error?: string; // Present on error
};

type InviteVerifyPayload = {
  token: string;
  otpCode?: string; // SMS only
  sessionInfo?: string; // SMS only
  previousSessionInfo?: string; // SMS only: the text before sessionInfo's
};

type InviteVerifyResponse = {
  success: boolean;
  person_id: string;
  firebase_uid: string;
  custom_token: string;
  message: string;
  error?: string; // Present on error
};

type RequestOtpPayload = {
  token: string;
  recaptchaToken: string;
};

type RequestOtpResponse = {
  success: boolean;
  sessionInfo?: string;
  error?: string;
};

type RecaptchaConfigResponse = {
  recaptchaSiteKey: string;
};

type VerificationStep =
  | "loading"
  | "lookup_error"
  | "sms_input"
  | "verifying"
  | "final_error"
  | "signing_in";

export default function JoinPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const {
    auth,
    user,
    loading: firebaseLoading,
    signInWithCustomToken,
  } = useFirebase();

  const [step, setStep] = useState<VerificationStep>("loading");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [sessions, setSessions] = useState<OtpSessions | null>(null);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [recaptchaSiteKey, setRecaptchaSiteKey] = useState<string | null>(null);
  const [isFetchingRecaptchaKey, setIsFetchingRecaptchaKey] = useState(false);

  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const hasRequestedCodeRef = useRef(false);

  const swrOptions = {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
  };
  const {
    data: lookupData,
    error: lookupError,
    isLoading: isLookupLoading,
  } = useGET(
    token ? `/invites/lookup/${token}` : null,
    undefined,
    swrOptions,
  ) as {
    data: InviteLookupResponse | undefined;
    error: NetworkError | undefined;
    isLoading: boolean;
  };

  const { trigger: triggerVerifyMutation, isMutating: isVerifyingInvite } =
    usePOST<InviteVerifyResponse, NetworkError, InviteVerifyPayload>(
      "/invites/verify",
    );

  const { trigger: triggerRequestOtp } = usePOST<
    RequestOtpResponse,
    NetworkError,
    RequestOtpPayload
  >("/invites/request-otp");

  // Drive the flow once the invite lookup settles.
  useEffect(() => {
    if (isLookupLoading || firebaseLoading) {
      setStep("loading");
      return;
    }

    if (lookupError) {
      console.error("Invite lookup failed (SWR):", lookupError);
      const apiError = lookupError.responseData?.error || lookupError.message;

      if (lookupError.responseData?.status === "used") {
        if (user) {
          navigate("/", { replace: true });
          return;
        }
        const identifier =
          lookupError.responseData.email ||
          lookupError.responseData.phone_number;
        navigate("/login", {
          replace: true,
          state: identifier ? { identifier } : undefined,
        });
        return;
      }

      Sentry.captureException(lookupError);
      setErrorMessage(
        apiError || "An unexpected error occurred during lookup.",
      );
      setStep("lookup_error");
      return;
    }

    if (lookupData) {
      if (!lookupData.success || !lookupData.method) {
        setErrorMessage(lookupData.error || "Invalid or expired invite link.");
        setStep("lookup_error");
        return;
      }

      if (lookupData.method === "email") {
        if (
          step !== "verifying" &&
          step !== "signing_in" &&
          !isVerifyingInvite
        ) {
          triggerEmailVerification(token!);
        }
      } else if (lookupData.method === "sms") {
        if (!lookupData.phone_number) {
          setErrorMessage(
            "Could not retrieve phone number for SMS verification.",
          );
          setStep("lookup_error");
          Sentry.captureMessage(
            "Lookup succeeded for SMS invite but phone_number was missing",
            { extra: { token } },
          );
        } else if (
          step !== "sms_input" &&
          step !== "verifying" &&
          step !== "signing_in"
        ) {
          setStep("sms_input");
        }
      }
    }
  }, [
    lookupData,
    lookupError,
    isLookupLoading,
    firebaseLoading,
    user,
    navigate,
    token,
    step,
    isVerifyingInvite,
  ]);

  // Fetch the reCAPTCHA site key once we know this is an SMS invite, so the
  // invisible widget is mounted before the first code request runs it.
  useEffect(() => {
    if (lookupData?.method !== "sms" || !lookupData.phone_number) return;
    if (recaptchaSiteKey || isFetchingRecaptchaKey) return;

    const fetchKey = async () => {
      setIsFetchingRecaptchaKey(true);
      try {
        const data = await fetchPublicAPI<RecaptchaConfigResponse>(
          "/auth/recaptcha-config",
        );
        if (!data.recaptchaSiteKey) {
          throw new Error("recaptchaSiteKey missing from response");
        }
        setRecaptchaSiteKey(data.recaptchaSiteKey);
      } catch (err) {
        console.error("Failed to fetch reCAPTCHA config:", err);
        Sentry.captureException(err);
        setErrorMessage("Could not start verification. Please try again.");
        setStep("lookup_error");
      } finally {
        setIsFetchingRecaptchaKey(false);
      }
    };
    fetchKey();
  }, [lookupData, recaptchaSiteKey, isFetchingRecaptchaKey]);

  // Auto-send the first code once the reCAPTCHA widget is mounted.
  useEffect(() => {
    if (
      lookupData?.method === "sms" &&
      lookupData.phone_number &&
      recaptchaSiteKey &&
      !hasRequestedCodeRef.current
    ) {
      hasRequestedCodeRef.current = true;
      handleRequestSMSCode();
    }
  }, [lookupData, recaptchaSiteKey]);

  const triggerEmailVerification = async (inviteToken: string) => {
    setErrorMessage(null);
    try {
      const data = await triggerVerifyMutation({ token: inviteToken });
      if (!data?.success || !data?.custom_token) {
        setErrorMessage(data?.error || "Failed to verify invite.");
        setStep("final_error");
        return;
      }
      await signInWithFirebase(data.custom_token, "/");
    } catch (err) {
      console.error("Error during verification flow:", err);
      if (!(err instanceof NetworkError)) {
        Sentry.captureException(err);
      }
      setErrorMessage(
        "An unexpected error occurred during the verification process.",
      );
      setStep("final_error");
    }
  };

  const signInWithFirebase = async (
    customToken: string,
    redirectPath: string = "/",
  ) => {
    setStep("signing_in");
    setErrorMessage(null);
    try {
      if (!signInWithCustomToken) {
        throw new Error(
          "signInWithCustomToken function not available from Firebase context.",
        );
      }
      await signInWithCustomToken(auth, customToken);
      navigate(redirectPath, { replace: true });
    } catch (err: any) {
      console.error("Firebase custom token sign-in failed:", err);
      Sentry.captureException(err);
      setErrorMessage(`Sign-in failed: ${err.message || "Unknown error"}`);
      setStep("final_error");
    }
  };

  /** Sends the invite's code; resolves true when one was sent. */
  const handleRequestSMSCode = async (
    trigger: SendTrigger = "resend",
  ): Promise<boolean> => {
    if (!token) return false;
    setErrorMessage(null);
    setIsSendingCode(true);
    try {
      const recaptcha = await freshRecaptchaToken(recaptchaRef);
      if (!recaptcha.ok) {
        setErrorMessage(grantFailureMessage("send_failed", INVITE_FALLBACK));
        return false;
      }

      const response = await triggerRequestOtp({
        token,
        recaptchaToken: recaptcha.token,
      });
      if (response?.sessionInfo) {
        const sent = response.sessionInfo;
        setSessions((held) =>
          trigger === "code_ended"
            ? restartOtpSessions(sent)
            : recordOtpSession(held, sent),
        );
        return true;
      }
      setErrorMessage(response?.error || "Failed to send verification code.");
      return false;
    } catch (err) {
      console.error("Error requesting SMS code:", err);
      if (err instanceof NetworkError) {
        const wording =
          trigger === "code_ended"
            ? endedCodeSendFailureMessage
            : smsRequestFailureMessage;
        setErrorMessage(
          wording(apiErrorCode(err), err.statusCode, INVITE_FALLBACK),
        );
      } else {
        Sentry.captureException(err);
        setErrorMessage(grantFailureMessage("send_failed", INVITE_FALLBACK));
      }
      return false;
    } finally {
      setIsSendingCode(false);
      setStep("sms_input");
    }
  };

  const handleVerifySMSCode = async (code: string): Promise<SubmitOutcome> => {
    if (!token) return undefined;
    if (!sessions) {
      setErrorMessage(grantFailureMessage("code_expired", INVITE_FALLBACK));
      setStep("sms_input");
      return undefined;
    }

    setErrorMessage(null);
    try {
      const data = await triggerVerifyMutation({
        token,
        otpCode: code,
        ...otpSessionFields(sessions),
      });
      if (!data?.success || !data?.custom_token) {
        setErrorMessage(data?.error || "Invalid verification code.");
        setStep("sms_input");
        return undefined;
      }
      await signInWithFirebase(data.custom_token, "/");
    } catch (err) {
      // Wrong/expired codes and phone mismatches are recoverable — keep the
      // user on the entry screen so they can retry or request a new code.
      if (err instanceof NetworkError) {
        setStep("sms_input");
        if (
          grantFailureKind(apiErrorCode(err), err.statusCode) === "code_expired"
        ) {
          return "code_ended";
        }
        setErrorMessage(
          smsVerifyFailureMessage(
            apiErrorCode(err),
            err.statusCode,
            INVITE_FALLBACK,
          ),
        );
        return undefined;
      }
      console.error("Error during SMS verification flow:", err);
      Sentry.captureException(err);
      setErrorMessage("An unexpected error occurred. Please try again.");
      setStep("final_error");
    }
    return undefined;
  };

  const renderContent = () => {
    if (isLookupLoading || step === "loading") {
      return (
        <div className="flex justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-cantelope border-t-transparent" />
        </div>
      );
    }

    switch (step) {
      case "lookup_error":
      case "final_error":
        return (
          <div className="space-y-4 text-center">
            <p className="text-lg font-semibold text-red-700">Error</p>
            <p className="text-gray-600">
              {errorMessage || "An unknown error occurred."}
            </p>
            <SupportCallLine
              kind="first_call"
              surface="login"
              screen="invite"
            />
          </div>
        );
      case "sms_input":
        if (!lookupData?.phone_number) {
          return (
            <p className="text-center text-red-500">
              Error: Phone number not available.
            </p>
          );
        }
        return (
          <OtpVerification
            type="sms"
            destinationDisplay={formatPhoneNumber(lookupData.phone_number)}
            onSubmit={handleVerifySMSCode}
            onResend={handleRequestSMSCode}
            error={errorMessage}
            isLoading={isSendingCode || isVerifyingInvite}
          />
        );
      case "verifying":
      case "signing_in":
        return (
          <div className="flex flex-col items-center space-y-2">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-cantelope border-t-transparent" />
            <p className="text-gray-600">
              {isVerifyingInvite ? "Verifying invite..." : "Signing you in..."}
            </p>
          </div>
        );
      default:
        setErrorMessage("Invalid application state.");
        setStep("final_error");
        return null;
    }
  };

  return (
    <AuthLayout title="Accept Invite" description="Complete the steps to join">
      {recaptchaSiteKey && (
        <ReCAPTCHA
          ref={recaptchaRef}
          size="invisible"
          sitekey={recaptchaSiteKey}
        />
      )}
      {renderContent()}
    </AuthLayout>
  );
}
